/**
 * Typed access to the inline early error buffer declared in `src/index.html`.
 *
 * The inline script starts collecting uncaught errors, unhandled rejections and `console.error`
 * calls at t=0, long before the lazily loaded Sentry SDK exists. Everything collected there is
 * replayed once the SDK is initialized, then the buffer is detached.
 */

export interface IEarlyErrorEntry {
    kind: 'error' | 'unhandledrejection';
    value: unknown;
    ts: number;
}

export interface IEarlyConsoleEntry {
    kind: 'console';
    args: unknown[];
    ts: number;
}

export type TEarlyBufferEntry = IEarlyErrorEntry | IEarlyConsoleEntry;

interface IEarlyBufferListeners {
    error: (event: ErrorEvent) => void;
    unhandledrejection: (event: PromiseRejectionEvent) => void;
    consolePatch: (...args: unknown[]) => void;
}

export interface IEarlyErrorBuffer {
    entries: TEarlyBufferEntry[];
    consoleError: (...args: unknown[]) => void;
    onError: (() => void) | null;
    listeners: IEarlyBufferListeners | null;
}

declare global {
    interface Window {
        __errBuf?: IEarlyErrorBuffer;
    }
}

export function getEarlyErrorBuffer(): IEarlyErrorBuffer | undefined {
    return typeof window === 'undefined' ? undefined : window.__errBuf;
}

/**
 * `console.error` is patched by the inline buffer, so anything logged through it would be collected
 * again. Callers that log deliberately (the Angular error handler) must use the original.
 */
export function getOriginalConsoleError(): (...args: unknown[]) => void {
    const buffer = getEarlyErrorBuffer();

    return buffer ? buffer.consoleError : console.error.bind(console);
}

/**
 * Detaches the early listeners, restores `console.error` and returns everything that was collected.
 * Must run *before* the SDK's `breadcrumbsIntegration` wraps `console.error`, otherwise the early
 * patch ends up wrapped rather than replaced and is never removed from the call chain.
 */
export function takeAndDetachEarlyErrorBuffer(): TEarlyBufferEntry[] {
    const buffer = getEarlyErrorBuffer();

    if (!buffer?.listeners) {
        return buffer?.entries ?? [];
    }

    window.removeEventListener('error', buffer.listeners.error);
    window.removeEventListener('unhandledrejection', buffer.listeners.unhandledrejection);

    if (console.error === buffer.listeners.consolePatch) {
        console.error = buffer.consoleError;
    }

    const entries = buffer.entries;

    buffer.listeners = null;
    buffer.onError = null;
    buffer.entries = [];

    return entries;
}
